import React, { useEffect, useRef } from 'react';
import s from './HoldingsList.module.scss';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Icon from '../Icon/Icon';
import iconType from '../../utils/icontype';

const HoldingsList = ({
    holdings = [],
    activeHoldingId,
    setActiveHolding,
    itemRefs,
    modifiers = [],
    allHoldingsLoaded = false,
    isLoading = false,
    isListView = false,
}) => {
    const hasScrolledRef = useRef(false);

    const handleItemClick = (id) => {
        setActiveHolding(id);

        const url = new URL(window.location);
        url.searchParams.set('holding', id);
        window.history.replaceState(null, '', url.toString());
    };

    useEffect(() => {
        if (!isListView) return;

        requestAnimationFrame(() => {
            const el = itemRefs.current[activeHoldingId];
            if (!el) return;

            const root = el.closest(`.${s.Root}`);
            const scrollContainer = root?.parentElement;

            if (scrollContainer) {
                scrollContainer.scrollTo({
                    top: el.offsetTop - 20,
                    behavior: 'smooth',
                });
            } else {
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        });
    }, [isListView, activeHoldingId, allHoldingsLoaded]);

    const classes = classNames(
        s.Root,
        modifiers.map((modifier) => s[`Root--${modifier}`])
    );

    return (
        <div className={classes}>
            <ul className={s.List}>
                {holdings
                    .slice()
                    .sort((a, b) => {
                        const aVal = a.value || a.name || '';
                        const bVal = b.value || b.name || '';
                        return aVal.localeCompare(bVal);
                    })
                    .map((holding) => {
                        const isActive = holding.sourceId === activeHoldingId;
                        const classes = classNames(s.Item, {
                            [s['Item--Active']]: isActive,
                        });
                        return (
                            <li
                                key={holding.sourceId}
                                className={classes}
                                ref={(el) =>
                                    (itemRefs.current[holding.sourceId] = el)
                                }>
                                <button
                                    className={s.Button}
                                    onClick={() =>
                                        handleItemClick(holding.sourceId)
                                    }>
                                    <div
                                        className={s.IconWrapper}
                                        data-test={holding.category}>
                                        <Icon
                                            name={iconType(holding.category)}
                                        />
                                    </div>
                                    <div className={s.TextWrapper}>
                                        <h2 className={s.Name}>
                                            {holding.value || holding.name}
                                        </h2>
                                        <p className={s.Address}>
                                            {holding.fullAddress ||
                                                holding.address}
                                        </p>
                                    </div>
                                    <Icon
                                        name="ArrowRight"
                                        className={s.ArrowIcon}
                                    />
                                </button>
                            </li>
                        );
                    })}
                {holdings.length === 0 && isLoading && (
                    <>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <li
                                key={`skeleton-${i}`}
                                className={s.SkeletonItem}
                                style={{ animationDelay: `${i * 80}ms` }}
                                aria-hidden="true">
                                <div className={s.SkeletonIcon} />
                                <div className={s.SkeletonText}>
                                    <div className={s.SkeletonName} />
                                    <div className={s.SkeletonAddress} />
                                </div>
                            </li>
                        ))}
                        <li className={s.SkeletonStatus} role="status">
                            <span className="sr-only">Loading holdings</span>
                        </li>
                    </>
                )}
                {holdings.length === 0 && !isLoading && (
                    <li className={s.NoResults}>
                        <p>No results found</p>
                    </li>
                )}
            </ul>
        </div>
    );
};

HoldingsList.propTypes = {
    holdings: PropTypes.arrayOf(
        PropTypes.shape({
            sourceId: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    activeHoldingId: PropTypes.string,
    setActiveHolding: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
};

export default HoldingsList;
